import type { Observation } from '@skyweather/weather-core'
import { resolveScreenFeatures, type ScreenFeatureSet } from '@skyweather/weather-core'
import { ageText, healthBadge, localTime } from './format.js'
import { nextReportDue } from './keepalive.js'
import type { SourceReading } from './types.js'

interface Props {
  reading: SourceReading
  trend: Observation[]
  timeZone: string
  now: number
  show?: ScreenFeatureSet
}

/**
 * The one header over every condition on the screen (Kyle, 2026-10-01: "anywhere we display conditions
 * that it's under the header of 'weather observations (source)'"). It is the API's own header for the
 * feed on screen, with the reading's age or a Late / Not reporting badge. Panels below it carry no header
 * of their own. A forecast is not an observation, so the forecast and winds-aloft panels keep theirs.
 *
 * A source that reports on a slow fixed cadence (an airport's automated station) also says when its next
 * report is due, so numbers that have not moved read as waiting, not frozen.
 */
export function ObservationsHeader({ reading, trend, timeZone, now, show = resolveScreenFeatures(undefined) }: Props) {
  const badge = healthBadge(reading.health, reading.healthSince, now)
  const due = reading.health === 'reporting' ? nextReportDue(trend.map((t) => t.observedAt), now) : null
  const dueText = show.nextReport && due !== null && due.cadenceMs >= 5 * 60_000
    ? ` · reports every ${Math.round(due.cadenceMs / 60_000)} min, next ${now < due.dueAt ? `due ${localTime(due.dueAt, timeZone)}` : 'due now'}`
    : ''
  return (
    <div className="obs-head">
      <h2>{reading.header}</h2>
      {badge ? <span className={`badge badge--${reading.health}`}>{badge}</span> : <span className="age">{ageText(reading.observation?.observedAt ?? null, now)}{dueText}</span>}
    </div>
  )
}
